import { getWalletAddress, setWalletAddress, getLoginWalletProviderId } from '@/utils/auth-token';
import { getUsdcContractAddress } from '@/utils/trade-contracts';
import { getWalletProviderById, getWalletSigningProvider } from '@/utils/wallet-provider';
import { resolveFastWalletTargetsForBalance } from '@/utils/wallet-address-sources';
import {
  fetchActiveWalletUsdcBalance,
  readUsdcBalanceForAddress,
} from '@/utils/wallet-balance-summary';

export { getWalletSigningProvider };
export {
  fetchActiveWalletUsdcBalance,
  fetchAllWalletsUsdcBalance,
  fetchUserWalletBalanceSummary,
  readUsdcBalanceForAddress,
} from '@/utils/wallet-balance-summary';

const TOKEN_META = {
  USDC: { color: '#2775ca', isStable: true, decimals: 6 },
};

function normalizeAddress(value) {
  return String(value ?? '').trim();
}

/** 展示用短地址，如 ...a1b2 */
export function formatWalletAddressSuffix(address) {
  const raw = normalizeAddress(address);
  if (raw.length < 8) return raw || '—';
  return `...${raw.slice(-4)}`;
}

function buildTokenRow(symbol, amount) {
  const meta = TOKEN_META[symbol] || {
    color: '#64748b',
    isStable: false,
    decimals: 18,
  };
  const safeAmount = Number.isFinite(amount) && amount >= 0 ? amount : 0;
  return {
    symbol,
    amount: safeAmount,
    usd: meta.isStable ? safeAmount : 0,
    color: meta.color,
    isStable: meta.isStable,
  };
}

/**
 * 读取当前平台钱包地址及链上余额（托管 Privy 地址）
 * 地址优先走本地缓存；余额 RPC 超时/失败时回落为 0，不阻断充值地址展示。
 */
export async function fetchWalletDepositBalances() {
  if (!getUsdcContractAddress()) {
    throw new Error('USDC contract not configured');
  }

  const targets = await resolveFastWalletTargetsForBalance();
  const address = normalizeAddress(targets[0]?.address) || normalizeAddress(getWalletAddress());
  if (!address) {
    throw new Error('wallet address not found');
  }

  let amount = 0;
  try {
    amount = await readUsdcBalanceForAddress(address);
  } catch (err) {
    console.warn('[wallet-balance] deposit balance read failed', address, err);
  }

  setWalletAddress(address);

  return {
    address,
    tokens: [buildTokenRow('USDC', amount)],
  };
}

/**
 * Connected 充值：读外接插件地址与 USDC（不覆盖平台托管地址缓存）
 * @param {string} [preferredProviderId]
 */
export async function fetchInjectedWalletDepositBalances(preferredProviderId) {
  if (!getUsdcContractAddress()) {
    throw new Error('USDC contract not configured');
  }

  const providerId = String(preferredProviderId || getLoginWalletProviderId() || 'metamask')
    .trim()
    .toLowerCase();

  let provider = getWalletProviderById(providerId);
  if (!provider?.request) {
    provider = await getWalletSigningProvider(providerId);
  }
  if (!provider?.request) {
    throw new Error('injected wallet provider not found');
  }

  let accounts = [];
  try {
    accounts = await provider.request({ method: 'eth_accounts' });
  } catch {
    accounts = [];
  }
  if (!Array.isArray(accounts) || !accounts[0]) {
    accounts = await provider.request({ method: 'eth_requestAccounts' });
  }
  const address = normalizeAddress(accounts?.[0]);
  if (!address) {
    throw new Error('wallet address not found');
  }

  let amount = 0;
  try {
    amount = await readUsdcBalanceForAddress(address);
  } catch (err) {
    console.warn('[wallet-balance] injected deposit balance read failed', address, err);
  }

  return {
    address,
    providerId,
    tokens: [buildTokenRow('USDC', amount)],
  };
}

/** 当前登录钱包链上 USDC */
export async function fetchConnectedWalletUsdcBalance() {
  return fetchActiveWalletUsdcBalance();
}

/** 当前登录方式对应钱包的链上 USDC（充值/交易弹窗等场景） */
export async function fetchLoginWalletUsdcBalance() {
  try {
    const { tokens } = await fetchWalletDepositBalances();
    const usdc = tokens.find((row) => row.symbol === 'USDC');
    const amount = Number(usdc?.amount ?? 0);
    if (Number.isFinite(amount)) return amount;
  } catch (err) {
    console.warn('[wallet-balance] login wallet read failed, fallback to summary', err);
  }
  return fetchActiveWalletUsdcBalance();
}

/** @deprecated 使用 fetchConnectedWalletUsdcBalance */
export async function fetchEmbeddedWalletUsdcBalance() {
  return fetchConnectedWalletUsdcBalance();
}
